import axios from "axios";
import { getAttribution, getVisitorId, getSessionId, track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

export function errText(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  if (detail.msg) return detail.msg;
  return String(detail);
}

export { API };

export function fireEvent(type, share_id) {
  // best-effort, never blocks UI
  return api.post("/analytics/event", { type, share_id }).catch(() => {});
}

export async function createShare({ issues = [], labels = [], verses = [], anonymous = false }) {
  const clean = verses.map((v) => ({
    reference: v.reference, text: v.text, translation: v.translation,
    explanation: v.explanation, application: v.application, prayer: v.prayer, related: v.related,
  }));
  const { data } = await api.post("/share", { issues, labels, verses: clean, anonymous });
  return { ...data, url: `${window.location.origin}${data.path}` };
}

// Starts the I Need a Proverb -> LifeGuide SSO handoff.
// Reliable same-tab redirect (no popup blocker) + one refresh-retry if the access cookie expired.
export async function startLifeguideHandoff(context = {}) {
  const { data: st } = await api.get("/lifeguide/status");
  if (!st.configured) return { ok: false, reason: "not_configured" };
  const attribution = getAttribution();
  const visitor_id = getVisitorId();
  const session_id = getSessionId();
  // Safe return route: the caller may pass context.return_path; otherwise use the current path
  // (never the query string — we never put free text / personal details in the return URL).
  const return_path = typeof context.return_path === "string" ? context.return_path : window.location.pathname;
  let res;
  try {
    res = await api.post("/lifeguide/handoff", { context, attribution, visitor_id, session_id, return_path });
  } catch (e) {
    if (e.response?.status === 401) {
      await api.post("/auth/refresh");
      res = await api.post("/lifeguide/handoff", { context, attribution, visitor_id, session_id, return_path });
    } else {
      throw e;
    }
  }
  if (res.data?.url) {
    const fromShare = !!localStorage.getItem("ipf_ref");
    await fireEvent("lifeguide_click");
    if (fromShare) await fireEvent("lifeguide_click_from_share");
    track("lifeguide_click", { path: window.location.pathname, topic: context.topic, feature: context.feature });
    let url = res.data.url;
    // Non-sensitive deep-link hint so LifeGuide can continue to a specific feature
    // (e.g. Say It Better) after any required welcome/auth/trial step.
    if (context.feature) {
      const sep = url.includes("?") ? "&" : "?";
      url = `${url}${sep}dest=${encodeURIComponent(context.feature)}`;
    }
    window.location.assign(url); // full same-tab navigation -> LifeGuide signs the user in
    return { ok: true };
  }
  return { ok: false, reason: "no_url" };
}
